import { useUpdateIncidentPriority } from '../../shared/hooks/useUpdateIncidentPriority'
import { usePermissions } from '@/hooks/usePermissions'

import { ChevronDownIcon } from '@chakra-ui/icons'
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react'

import { PriorityIcon } from '@/components/icons'
import { MenuSelectItem } from '@/components/ui'
import { IncidentQuery } from '@/graphql/generated/operations'
import { IncidentPriority } from '@/graphql/generated/schemas'
import { priorityOptions } from '@/redux/incidents/incidentsFilterOptions'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import {
  getIncidentPriorityColor,
  getIncidentPriorityDisplayName,
} from '@/utils/incidents'

interface IncidentPriorityDropdownIProps {
  incident?: IncidentQuery['incident']
}

export const IncidentPriorityDropdown = ({
  incident,
}: IncidentPriorityDropdownIProps) => {
  const { shouldEnableIncidentActions } = usePermissions()
  const { updatePriority, currentPriority, isUpdating } =
    useUpdateIncidentPriority({
      priority: incident?.priority,
    })

  const handleSelect = (priority: IncidentPriority) => {
    if (incident?.id && priority !== currentPriority) {
      trackEvent(`${BUTTON_PRESS} Manually set Incident Priority`, {
        newValue: priority,
        oldValue: currentPriority,
        incidentId: incident.id,
        incidentDisplayId: incident?.displayId,
      })

      updatePriority(incident.id, priority)
    }
  }

  const tooltipLabel = currentPriority
    ? `${getIncidentPriorityDisplayName(currentPriority)} Priority`
    : 'Set Priority'

  return (
    <Menu closeOnSelect isLazy placement='bottom-end'>
      <Tooltip hasArrow label={tooltipLabel} placement='top'>
        <MenuButton
          _focus={{ bgColor: 'none' }}
          _hover={{ opacity: 0.8 }}
          alignContent='center'
          alignItems='center'
          as={Button}
          bg='transparent'
          border='1px solid rgba(0,0,0,0.2)'
          borderRadius='6px'
          cursor='pointer'
          data-testid='incident-priority-dropdown'
          display='flex'
          h='25px'
          justifyContent='center'
          minW='34px'
          onFocus={(e) => e.preventDefault()}
          px='6px'
          rightIcon={<ChevronDownIcon />}
        >
          {isUpdating ? (
            <Spinner size='xs' />
          ) : (
            <PriorityIcon
              priority={currentPriority}
              showTextIfNoPriority={false}
            />
          )}
        </MenuButton>
      </Tooltip>
      <MenuList minW='120px' p={0} zIndex={9999}>
        <Text
          borderBottom='1px solid rgba(0,0,0,0.1)'
          color='gray.600'
          fontSize='12px'
          px='18px'
          py='8px'
        >
          Priority
        </Text>
        {priorityOptions.map((option, index) => {
          const isSelected = currentPriority === option.value.value
          return (
            <MenuSelectItem
              bgColor={
                isSelected
                  ? getIncidentPriorityColor(option.value.value)
                  : undefined
              }
              icon={
                <PriorityIcon
                  priority={priorityOptions[index].value.value}
                  showTextIfNoPriority={false}
                />
              }
              isDisabled={
                isUpdating || isSelected || !shouldEnableIncidentActions
              }
              key={option.value.value}
              label={option.label}
              onClick={() => handleSelect(option.value.value)}
              showDivider={index < priorityOptions.length - 1}
            />
          )
        })}
      </MenuList>
    </Menu>
  )
}
